import { Component, Input, OnInit, OnDestroy, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';

// PrimeNG imports
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { PanelMenuModule } from 'primeng/panelmenu';

// Services
import { ProjectService } from '../../services/project.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-sidenav',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    DividerModule,
    ButtonModule,
    PanelMenuModule,
  ],
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.css',
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input() visible = false;
  menuItems: MenuItem[] = [];
  private destroy$ = new Subject<void>();

  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly projectService = inject(ProjectService);
  private readonly authService = inject(AuthService);

  constructor() {
    // React to changes in projects
    effect(() => {
      const projects = this.projectService.allProjects();
      this.updateMenuWithProjects(projects);
    });
  }

  ngOnInit(): void {
    // Initialize menu structure
    // Projects will be loaded by HomeComponent and this component
    // will react via the effect() when data becomes available
    this.initializeMenu();

    // Update menu when language changes
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.initializeMenu();
      this.updateMenuWithProjects(this.projectService.allProjects());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeMenu(): void {
    this.menuItems = [
      {
        label: this.translate.instant('navigation.projectInsights'),
        icon: 'pi pi-chart-line',
        items: [], // Will be populated by updateMenuWithProjects
      },
      {
        label: this.translate.instant('navigation.myProjects'),
        icon: 'pi pi-briefcase',
        routerLink: ['/projects'],
      },
    ];
  }

  private updateMenuWithProjects(projects: any[]): void {
    // Find the Project Insights menu item and update its children
    const projectInsightsMenu = this.menuItems.find(
      (item) => item.label === this.translate.instant('navigation.projectInsights')
    );

    if (projectInsightsMenu) {
      projectInsightsMenu.items = projects.map((project) => ({
        label: project.name,
        icon: 'pi pi-folder',
        routerLink: ['/insight', project.id],
      }));
    }
  }

  logout(): void {
    this.authService.logout(true).subscribe();
  }
}
